'use client';

import { useExtraClasses } from '@lib/hooks/useExtraClasses';
import Link from 'next/link';
import styles from './button.module.scss';
import { useMemo } from 'react';

// type of valid extra classes in the button stylesheet
type extraBtnClasses = 'wider' | 'wider-center' | 'eventButton' | 'whiteFocus' | '';

/**
 * Button component, used to create a link button with correct a or link tag
 *
 * @param props - The props of the component
 * @param props.href - The href of the button
 * @param props.text - The children of the button
 * @param props.target Optional - The target of the button
 * @param props.onClick Optional - The onClick function of the button
 * @param props.extraClasses Optional - The extra classes of the button - 'wider' | 'wider-center' | 'eventButton' | 'whiteFocus' | ''
 * @returns The Button component with correct a or link tag
 *
 * @example
 * ```tsx
 * <Button
 *  href="/"
 *  text="Home"
 *  target="_blank"
 *  onClick={() => console.log('Button clicked')}
 *  extraClasses={['wider']}
 * />
 * ```
 */
const Button = ({
    href,
    text,
    target = '_self',
    onClick,
    extraClasses,
    ...rest
}: {
    href: string;
    text: string;
    target?: '_blank' | '_self' | '_parent' | '_top';
    onClick?: () => void;
    extraClasses?: extraBtnClasses[];
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    // Check and memoise if the href is an external link
    const isExternal: boolean = useMemo(() => {
        const externalUrlPattern = /^(https?:|ftp:|mailto:|tel:)/i;
        return externalUrlPattern.test(href);
    }, [href]);  

    // Get the extra classes
    const extraClassString = useExtraClasses(extraClasses, styles);

    // If the link is external, use an <a> tag
    if (isExternal) {
        return (
            <a
                href={href}
                target={target}
                rel="noopener noreferrer"
                className={`${styles.btn} ${extraClassString}`}
                onClick={onClick}
                {...rest}
            >
                {text}
            </a>
        );
    }

    // If the link is internal, use a Next.js <Link> component
    return (
        <Link
            href={href}
            target={target}
            className={`${styles.btn} ${extraClassString}`}
            onClick={onClick && onClick}
            {...rest}
        >
            {text}
        </Link>
    );
};

export default Button;
